import { isProd } from "@configs/env";
import * as Sentry from "@sentry/react";
import { getDevFlags } from "@utils/dev-flags";
import logger from "@utils/logger";
import { MemberModel } from "kz-ui-sdk";
import { isNil, omitBy } from "lodash-es";
import moment from "moment";
import { useEffect } from "react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";

// Sentry configuration
const PROD_SAMPLE_RATE = 0.1;
const SANDBOX_SAMPLE_RATE = 1.0;

/**
 * Get current app version
 * Production/Staging: VITE_APP_VERSION - commit hash
 * Development: vYYMMDDHH - current date and time - rounded to nearest hour
 */
const getAppVersion = () => {
  return import.meta.env.VITE_APP_VERSION || moment().format("vYYMMDDHH");
};

const getSampleRate = () => {
  return isProd() ? PROD_SAMPLE_RATE : SANDBOX_SAMPLE_RATE;
};

const enableAutoCaptureSentry = isProd() || getDevFlags()?.enableSentry;

/**
 * Initialize Sentry
 * @description Initialize Sentry for error tracking
 * Only initialize in production
 */
export const initSentry = () => {
  try {
    // Enable only in production
    if (enableAutoCaptureSentry) {
      const version = getAppVersion();
      const sampleRate = getSampleRate();
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_BRAND_KEY,
        integrations: [
          Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          }),
          Sentry.browserTracingIntegration(),
          Sentry.captureConsoleIntegration({
            levels: ["error"],
          }),
        ],
        sampleRate: sampleRate,
        release: version,
        /**
         * Ignore some safe errors
         */
        ignoreErrors: [
          // https://forum.sentry.io/t/resizeobserver-loop-limit-exceeded/8402/4
          "ResizeObserver loop limit exceeded",
          // https://stackoverflow.com/questions/59074487/reference-error-cant-find-variable-intersectionobserver
          "Can't find variable: IntersectionObserver",
          // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/16
          "Non-Error promise rejection captured",
          "Non-Error exception captured",
          // In case app loads old service worker
          /TypeError: Script https?:\/\/[^/]+\/service-worker\.js load failed/,
          // Ignore network error
          "Unable to preload CSS",
          "Minified Redux Toolkit error #38",
          "The HTMLImageElement provided is in the 'broken' state",
          "Fetch is aborted",
          // Ignore web-vital if not supported
          "this.o.at is not a function",
          // CanvasGradient is not supported in some browsers
          "Failed to execute 'addColorStop' on 'CanvasGradient'",
          // Ignore error from browser policy
          "NotAllowedError: The requesting page is not visible",
          "NotAllowedError, The requesting page is not visible",
          "Permission was denied",
          "Wake Lock permission request denied",
          // Ignore noisy Statsig console error (not actionable)
          /\[Statsig\]\s+EvaluationsDataAdapter is not attached to a Client/,
          // Ignore dynamic import failures (handled by app recovery logic)
          /TypeError: Failed to fetch dynamically imported module/,
          // Ignore module script import failures (handled by app recovery logic)
          "TypeError: Importing a module script failed",
          // Ignore Statsig registry NetworkError only
          "TypeError: Failed to fetch",
          "TypeError: Load failed",
          /NetworkError: https?:\/\/prodregistryv2\.org\//,
          /NetworkError: https?:\/\/api\.statsig\.com\//,
          /NetworkError: https?:\/\/featuregates\.org\//,
          /NetworkError: https?:\/\/statsigapi\.net\//,
          /NetworkError: https?:\/\/events\.statsigapi\.net\//,
          /NetworkError: https?:\/\/api\.statsigcdn\.com\//,
          /NetworkError: https?:\/\/featureassets\.org\//,
          /NetworkError: https?:\/\/assetsconfigcdn\.org\//,
          /NetworkError: https?:\/\/cloudflare-dns\.com\//,
          /NetworkError: https?:\/\/beyondwickedmapping\.org\//,
          "FetchEvent.respondWith received an error: Returned response is null",
          "ERROR [Statsig] A networking error occurred",
          "Statsig",
          // Ignore pwa prompt on unsupported browser
          "NotAllowedError: Failed to execute 'prompt' on 'BeforeInstallPromptEvent': The prompt() method must be called with a user gesture",
          // Ignore 3rd party error
          "detectIncognito somehow failed to query storage quota",
          "detectIncognito cannot determine the browser",
          "Failed to execute 'request' on 'WakeLock'",
          "logMutedMessage",
          "xbrowser is not defined",
          "Java object is gone",
          "The operation was aborted",
          // Development environment errors
          "Warning",
          "[hmr]",
        ],
        beforeSend(event, hint) {
          // Ignore error from 3rd party/extension/game frame
          const frames = event?.exception?.values?.[0]?.stacktrace?.frames;
          const firstFrame = frames?.[0];
          if (firstFrame && firstFrame.filename === "<anonymous>") {
            return null;
          }

          // Event extra response is limited to 2 levels deep, so we need to merge the response from captureContext
          if (event.extra?.response) {
            event.extra.response = (hint?.captureContext as any)?.extra?.response || event.extra.response;
          }

          return event;
        },
        beforeBreadcrumb(breadcrumb, hint) {
          if (breadcrumb.category === "ui.click" && hint?.event?.target) {
            const ariaLabel = hint?.event?.target?.ariaLabel;
            const textContent = hint?.event?.target?.textContent || undefined;
            const name = hint?.event?.target?.name || undefined;
            breadcrumb.data = omitBy(
              {
                ...breadcrumb.data,
                textContent,
                ariaLabel,
                name,
              },
              isNil,
            );
          }

          return breadcrumb;
        },
      });
      Sentry.setContext("Dependencies", {
        "kz-ui-sdk": import.meta.env.KZ_UI_SDK_VERSION || "unknown",
      });
      Sentry.setTags({
        "group-id": import.meta.env.VITE_GROUP_ID,
        "account-id": import.meta.env.VITE_ACCOUNT_ID,
        "deployment-env": import.meta.env.VITE_DEPLOYMENT_ENV,
      });
      logger._console.log("Sentry initialized");
    }
  } catch (error) {
    logger._console.error("Sentry initialization failed:", error);
  }
};

/**
 * This member will be sent to Sentry reporting
 */
export const setSentryMember = (member?: MemberModel | null) => {
  if (enableAutoCaptureSentry) {
    if (!member) {
      // Clear user
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({
      id: member.id,
      username: member.phone + "-" + member.apiIdentifier,
    });
  }
};
